import React, { useEffect, useMemo, useState } from 'react';
import { Text as NativeText, useWindowDimensions } from 'react-native';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from './rn';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { QUICK_REACTION_EMOJIS, REACTION_EMOJIS } from '../data/reactionEmojis';
import type { Reaction } from '../utils/reactions';
import { getFrequentReactions } from '../services/reactionFrequency';

type Props = { userId?: number; reactions?: Reaction[]; onSelect: (emoji: string) => void };

export default function MessageReactionPicker({ userId, reactions, onSelect }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [frequent, setFrequent] = useState<{ userId: number; emojis: string[] } | null>(null);
  useEffect(() => {
    let active = true;
    if (userId) getFrequentReactions(userId).then(emojis => {
      if (active) setFrequent({ userId, emojis });
    }).catch(() => {});
    return () => { active = false; };
  }, [userId]);
  const quick = frequent && frequent.userId === userId ? frequent.emojis : QUICK_REACTION_EMOJIS;
  const colors = useTheme();
  const { t } = useTranslation();
  const { height, width } = useWindowDimensions();
  const [rowWidth, setRowWidth] = useState<number | null>(null);
  // Reserve a full touch target for +; remaining shortcuts stay available in the palette.
  const quickCount = Math.min(QUICK_REACTION_EMOJIS.length,
    Math.max(0, Math.floor((rowWidth ?? Math.max(0, width - 32)) / 44) - 1));
  const styles = useMemo(() => StyleSheet.create({
    quick: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 8 },
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, paddingVertical: 8 },
    button: { width: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center', borderRadius: 22 },
    active: { backgroundColor: colors.gray100 },
    emoji: { fontSize: 26, paddingVertical: 4, includeFontPadding: true },
    header: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    title: { flex: 1, color: colors.black, fontSize: 16, fontWeight: '600' },
    list: { maxHeight: Math.min(320, height * 0.38) },
  }), [colors, height]);
  const button = (emoji: string) => {
    const active = reactions?.some(r => r.mine && r.emoji === emoji) ?? false;
    return (
      <TouchableOpacity
        key={emoji}
        testID={`reaction-choice-${emoji}`}
        style={[styles.button, active && styles.active]}
        accessibilityRole="button"
        accessibilityState={{ selected: active }}
        accessibilityLabel={t(active ? 'message.reactionRemove' : 'message.reactionAdd', { emoji })}
        onPress={() => onSelect(emoji)}>
        <NativeText allowFontScaling={false} style={styles.emoji}>{emoji}</NativeText>
      </TouchableOpacity>
    );
  };
  if (!expanded) return (
    <View testID="reaction-quick-row" style={styles.quick}
      onLayout={({ nativeEvent }) => setRowWidth(nativeEvent.layout.width)}>
      {quick.slice(0, quickCount).map(button)}
      <TouchableOpacity
        testID="reaction-more"
        style={styles.button}
        accessibilityRole="button"
        accessibilityLabel={t('message.moreReactions')}
        accessibilityState={{ expanded: false }}
        onPress={() => setExpanded(true)}>
        <Ionicons name="add" size={20} color={colors.black} />
      </TouchableOpacity>
    </View>
  );
  return (
    <View testID="reaction-picker-expanded">
      <View style={styles.header}>
        <TouchableOpacity
          testID="reaction-picker-back"
          style={styles.button}
          accessibilityRole="button"
          accessibilityLabel={t('common.back')}
          onPress={() => setExpanded(false)}>
          <Ionicons name="chevron-back" size={22} color={colors.black} />
        </TouchableOpacity>
        <Text accessibilityRole="header" style={styles.title}>{t('message.moreReactions')}</Text>
      </View>
      <ScrollView testID="reaction-picker-scroll" style={styles.list} showsVerticalScrollIndicator>
        <View style={styles.grid}>{REACTION_EMOJIS.map(button)}</View>
      </ScrollView>
    </View>
  );
}
